import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CustomSnackbar from "../../components/CustomSnackbar";
import CanvasBoard from "../../../pages/puzzles/jigsaw/CanvasBoard";
import CanvasScattered from "../../../pages/puzzles/jigsaw/CanvasScattered";
import { BOARD_SIZE } from "../../../pages/puzzles/jigsaw/PieceVisual";
import { resolveJigsawTemplateTileSize } from "../../../pages/puzzles/jigsaw/jigsawBrowseUtils";
import AdminJigsawPlayService from "./AdminJigsawPlayService";
import AdminColorBasketTrays from "./AdminColorBasketTrays";

const DEFAULT_BASKET_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function normalizePiecePaths(jigsaw) {
    const raw = jigsaw?.piecePaths?.pieces ?? jigsaw?.piecePaths;
    if (!Array.isArray(raw)) return null;
    return raw.map((piece) => ({
        ...piece,
        path: piece?.path ?? piece?.d ?? null,
    }));
}

function playLayoutFromRecord(playRow) {
    const boardPieces = Array.isArray(playRow?.boardPositions) ? playRow.boardPositions : [];
    const scatterPositions =
        playRow?.scatteredPieces && typeof playRow.scatteredPieces === "object"
            ? playRow.scatteredPieces
            : {};
    return { boardPieces, scatterPositions };
}

function formatRegion(region) {
    if (!region || typeof region !== "object") return "—";
    const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(3) : "—");
    return `(${fmt(region.minXNorm)}, ${fmt(region.minYNorm)}) → (${fmt(region.maxXNorm)}, ${fmt(region.maxYNorm)}) · center (${fmt(region.centerXNorm)}, ${fmt(region.centerYNorm)})`;
}

export default function AdminJigsawPlayDetailPage() {
    const { playId } = useParams();
    const navigate = useNavigate();
    const boardRef = useRef(null);
    const scatteredRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [playRow, setPlayRow] = useState(null);
    const [error, setError] = useState(null);
    const [forceCount, setForceCount] = useState("5");
    const [forceBusy, setForceBusy] = useState(false);
    const [basketCount, setBasketCount] = useState(3);
    const [allowedBasketCounts, setAllowedBasketCounts] = useState(DEFAULT_BASKET_COUNTS);
    const [colorBasketMeta, setColorBasketMeta] = useState(null);
    const [colorBasketResult, setColorBasketResult] = useState(null);
    const [basketBusy, setBasketBusy] = useState(false);
    const [showColorBasketsOnBoard, setShowColorBasketsOnBoard] = useState(false);
    const [boardBasketBusy, setBoardBasketBusy] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const loadDetail = useCallback(async () => {
        if (!playId) return;
        setLoading(true);
        setError(null);
        try {
            const row = await AdminJigsawPlayService.requestPlayDetail(playId);
            setPlayRow(row);
        } catch (e) {
            setError(e?.message || "Failed to load play session");
            setPlayRow(null);
        } finally {
            setLoading(false);
        }
    }, [playId]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    const jigsawId = playRow?.jigsawId ?? playRow?.jigsaw?.id ?? null;

    useEffect(() => {
        let cancelled = false;
        setColorBasketResult(null);
        setColorBasketMeta(null);
        setShowColorBasketsOnBoard(false);
        if (!jigsawId) {
            setAllowedBasketCounts(DEFAULT_BASKET_COUNTS);
            return undefined;
        }

        (async () => {
            try {
                const meta = await AdminJigsawPlayService.getColorBaskets(jigsawId);
                if (cancelled) return;
                setColorBasketMeta(meta);
                const allowed = Array.isArray(meta?.allowedCounts) && meta.allowedCounts.length > 0
                    ? meta.allowedCounts
                    : DEFAULT_BASKET_COUNTS;
                setAllowedBasketCounts(allowed);
                setBasketCount((prev) => (allowed.includes(prev) ? prev : allowed[0]));
            } catch {
                if (cancelled) return;
                setAllowedBasketCounts(DEFAULT_BASKET_COUNTS);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [jigsawId]);

    const jigsaw = playRow?.jigsaw ?? null;
    const grid = useMemo(() => {
        if (!jigsaw) return 0;
        const fromTemplate = resolveJigsawTemplateTileSize(jigsaw);
        if (fromTemplate > 0) return fromTemplate;
        const pieces = jigsaw.piecePaths?.pieces;
        if (Array.isArray(pieces) && pieces.length > 0) {
            const n = Math.round(Math.sqrt(pieces.length));
            if (n * n === pieces.length) return n;
        }
        const direct = Number(jigsaw.tileSize);
        return Number.isFinite(direct) && direct > 0 ? direct : 0;
    }, [jigsaw]);

    const paths = useMemo(() => normalizePiecePaths(jigsaw), [jigsaw]);
    const svgPathFallback = jigsaw?.svgPath ?? jigsaw?.svg_path ?? null;
    const imageUrl = playRow?.imageUrl ?? null;

    const { boardPieces, scatterPositions } = useMemo(
        () => playLayoutFromRecord(playRow),
        [playRow],
    );

    const total = grid > 0 ? grid * grid : 0;
    const piecesNotOnBoard = useMemo(() => {
        if (total <= 0) return [];
        const onBoard = new Set(boardPieces.map((b) => b.pieceIndex));
        return Array.from({ length: total }, (_, i) => i).filter((i) => !onBoard.has(i));
    }, [boardPieces, total]);

    const placedCount = boardPieces.length;
    const isSolved = placedCount === total && total > 0;

    const handleForcePlace = async () => {
        if (!playRow?.id || playRow.isCompleted) return;
        const n = Number(forceCount);
        if (!Number.isInteger(n) || n <= 0) {
            setSnackbar({ open: true, severity: "warning", message: "Enter a positive piece count." });
            return;
        }

        setForceBusy(true);
        try {
            await AdminJigsawPlayService.forcePlacePieces(playRow.id, n);
            await loadDetail();
            setSnackbar({
                open: true,
                severity: "success",
                message: `Force-placed up to ${n} piece${n === 1 ? "" : "s"}.`,
            });
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.message || "Force place failed. Requires local backend with admin force-place support.",
            });
        } finally {
            setForceBusy(false);
        }
    };

    const handleGenerateColorBaskets = async () => {
        if (!jigsawId) {
            setSnackbar({ open: true, severity: "warning", message: "This play has no jigsaw id." });
            return;
        }
        setBasketBusy(true);
        try {
            const result = await AdminJigsawPlayService.recomputeColorBaskets(jigsawId, basketCount);
            setColorBasketResult(result);
            if (result?.available === false) {
                setShowColorBasketsOnBoard(false);
                setSnackbar({
                    open: true,
                    severity: "warning",
                    message: `Color baskets unavailable: ${result.reason || "unknown reason"}`,
                });
            } else {
                const n = Array.isArray(result?.baskets) ? result.baskets.length : 0;
                setShowColorBasketsOnBoard(n > 0);
                setSnackbar({
                    open: true,
                    severity: "success",
                    message: `Generated ${n} color basket${n === 1 ? "" : "s"} (K=${result?.basketCount ?? basketCount}).`,
                });
            }
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.message || "Failed to generate color baskets.",
            });
        } finally {
            setBasketBusy(false);
        }
    };

    const handleShowColorBasketsOnBoard = async () => {
        if (!jigsawId) {
            setSnackbar({ open: true, severity: "warning", message: "This play has no jigsaw id." });
            return;
        }
        if (showColorBasketsOnBoard) {
            setShowColorBasketsOnBoard(false);
            return;
        }

        const existing =
            colorBasketResult?.available !== false &&
            Number(colorBasketResult?.basketCount) === Number(basketCount) &&
            Array.isArray(colorBasketResult?.baskets) &&
            colorBasketResult.baskets.length > 0
                ? colorBasketResult
                : null;

        if (existing) {
            setShowColorBasketsOnBoard(true);
            return;
        }

        setBoardBasketBusy(true);
        try {
            const result = await AdminJigsawPlayService.getColorBaskets(jigsawId, basketCount);
            setColorBasketResult(result);
            if (result?.available === false) {
                setShowColorBasketsOnBoard(false);
                setSnackbar({
                    open: true,
                    severity: "warning",
                    message: `Color baskets unavailable: ${result.reason || "unknown reason"}`,
                });
                return;
            }
            const n = Array.isArray(result?.baskets) ? result.baskets.length : 0;
            if (n === 0) {
                setShowColorBasketsOnBoard(false);
                setSnackbar({
                    open: true,
                    severity: "warning",
                    message: "No baskets returned. Try Generate color baskets first.",
                });
                return;
            }
            setShowColorBasketsOnBoard(true);
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.message || "Failed to load color baskets.",
            });
        } finally {
            setBoardBasketBusy(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !playRow) {
        return (
            <Stack spacing={2}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/jigsaw/play")}>
                    Back to Play list
                </Button>
                <Typography color="error">{error || "Play session not found"}</Typography>
            </Stack>
        );
    }

    const baskets = Array.isArray(colorBasketResult?.baskets) ? colorBasketResult.baskets : [];

    return (
        <Stack spacing={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/jigsaw/play")}>
                    Back
                </Button>
                <Typography variant="h5" sx={{ flex: 1, minWidth: 200 }}>
                    {playRow.puzzleTitle}
                </Typography>
                <Chip
                    label={playRow.status}
                    color={playRow.isCompleted ? "success" : "warning"}
                    variant="outlined"
                />
            </Box>

            <Paper sx={{ p: 2 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            User
                        </Typography>
                        <Typography variant="body2">{playRow.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {playRow.email}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Started
                        </Typography>
                        <Typography variant="body2">{playRow.startedAt || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Finished
                        </Typography>
                        <Typography variant="body2">{playRow.finishedAt || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Attempts
                        </Typography>
                        <Typography variant="body2">{playRow.attemptsCount}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            On board
                        </Typography>
                        <Typography variant="body2">
                            {placedCount}/{total || "—"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            In tray
                        </Typography>
                        <Typography variant="body2">{piecesNotOnBoard.length}</Typography>
                    </Grid>
                    {jigsaw?.templateName && (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Template
                            </Typography>
                            <Typography variant="body2">{jigsaw.templateName}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {!playRow.isCompleted && (
                <Paper sx={{ p: 2 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                        <TextField
                            label="Pieces to place"
                            type="number"
                            size="small"
                            value={forceCount}
                            onChange={(e) => setForceCount(e.target.value)}
                            slotProps={{ htmlInput: { min: 1 } }}
                            sx={{ width: { xs: "100%", sm: 160 } }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleForcePlace}
                            disabled={forceBusy || !paths || !imageUrl}
                        >
                            {forceBusy ? "Placing…" : "Force place pieces"}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                            Local backend only — snaps off-board pieces onto home cells for this user&apos;s session.
                        </Typography>
                    </Stack>
                </Paper>
            )}

            {(!paths || !imageUrl || grid <= 0) && (
                <Typography color="warning.main">
                    Puzzle template or story image is missing — board preview unavailable.
                </Typography>
            )}

            {paths && imageUrl && grid > 0 && (
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Paper sx={{ p: 2 }}>
                            <Box sx={{ maxWidth: BOARD_SIZE, mx: "auto" }}>
                                <CanvasBoard
                                    boardRef={boardRef}
                                    boardPieces={boardPieces}
                                    grid={grid}
                                    imageUrl={imageUrl}
                                    paths={paths}
                                    svgPathFallback={svgPathFallback}
                                    dragging={null}
                                    placedCount={placedCount}
                                    total={total}
                                    isSolved={isSolved}
                                    onBoardPieceMouseDown={() => {}}
                                    readOnly
                                    showPieceGridCoords={false}
                                    headerTitle="Board (saved layout)"
                                />
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ p: 2 }}>
                            <CanvasScattered
                                scatteredRef={scatteredRef}
                                piecesNotOnBoard={piecesNotOnBoard}
                                scatterPositions={scatterPositions}
                                grid={grid}
                                imageUrl={imageUrl}
                                paths={paths}
                                dragging={null}
                                onScatteredPieceMouseDown={() => {}}
                                readOnly
                                showPieceGridCoords={false}
                                showViewToggle={false}
                                trayDescription="Off-board pieces from saved session"
                            />
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="h6" sx={{ flex: 1 }}>
                            Color baskets
                        </Typography>
                        {colorBasketResult && (
                            <>
                                <Chip
                                    size="small"
                                    label={colorBasketResult.available ? "Available" : "Unavailable"}
                                    color={colorBasketResult.available ? "success" : "warning"}
                                    variant="outlined"
                                />
                                {colorBasketResult.basketCount != null && (
                                    <Chip size="small" label={`K=${colorBasketResult.basketCount}`} />
                                )}
                                {colorBasketResult.algorithmVersion && (
                                    <Chip size="small" variant="outlined" label={colorBasketResult.algorithmVersion} />
                                )}
                                {colorBasketResult.requiresUnlock && (
                                    <Chip size="small" color="info" variant="outlined" label="Requires unlock" />
                                )}
                            </>
                        )}
                    </Box>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="color-basket-count-label">Basket count</InputLabel>
                            <Select
                                labelId="color-basket-count-label"
                                label="Basket count"
                                value={basketCount}
                                onChange={(e) => {
                                    setBasketCount(Number(e.target.value));
                                    setShowColorBasketsOnBoard(false);
                                }}
                                disabled={basketBusy || boardBasketBusy || !jigsawId}
                            >
                                {allowedBasketCounts.map((k) => (
                                    <MenuItem key={k} value={k}>
                                        {k}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant={showColorBasketsOnBoard ? "outlined" : "contained"}
                            onClick={handleShowColorBasketsOnBoard}
                            disabled={boardBasketBusy || basketBusy || !jigsawId}
                        >
                            {boardBasketBusy
                                ? "Loading…"
                                : showColorBasketsOnBoard
                                  ? "Hide baskets"
                                  : "Show baskets"}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleGenerateColorBaskets}
                            disabled={basketBusy || boardBasketBusy || !jigsawId}
                        >
                            {basketBusy ? "Generating…" : "Generate (recompute)"}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                            Jigsaw {jigsawId || "—"}
                            {colorBasketMeta?.available === false
                                ? ` — currently unavailable (${colorBasketMeta.reason || "unknown"})`
                                : ""}
                        </Typography>
                    </Stack>

                    {colorBasketResult && (
                        <Typography variant="body2" color="text.secondary">
                            tileSize={colorBasketResult.tileSize ?? "—"} · pieces=
                            {colorBasketResult.pieceCount ?? "—"}
                            {colorBasketResult.reason ? ` · reason=${colorBasketResult.reason}` : ""}
                        </Typography>
                    )}

                    {showColorBasketsOnBoard && baskets.length > 0 && paths && imageUrl && grid > 0 && (
                        <AdminColorBasketTrays
                            baskets={baskets}
                            grid={grid}
                            imageUrl={imageUrl}
                            paths={paths}
                        />
                    )}

                    {colorBasketResult && (
                        baskets.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No baskets returned.
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Index</TableCell>
                                        <TableCell>Color</TableCell>
                                        <TableCell>Pieces</TableCell>
                                        <TableCell>Piece IDs</TableCell>
                                        <TableCell>Region (norm)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {baskets.map((basket) => {
                                        const hex = basket?.dominantColorHex || "#999999";
                                        const pieceIds = Array.isArray(basket?.pieceIds) ? basket.pieceIds : [];
                                        return (
                                            <TableRow key={basket?.index ?? hex}>
                                                <TableCell>{basket?.index}</TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Box
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: 0.5,
                                                                bgcolor: hex,
                                                                border: "1px solid",
                                                                borderColor: "divider",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography variant="body2">{hex}</Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>{pieceIds.length}</TableCell>
                                                <TableCell sx={{ maxWidth: 360, wordBreak: "break-word" }}>
                                                    <Typography variant="caption" component="span">
                                                        {pieceIds.join(", ")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" component="span">
                                                        {formatRegion(basket?.region)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )
                    )}
                </Stack>
            </Paper>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((prev) => ({ ...prev, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Stack>
    );
}
